import { useState, useEffect } from'react';
import { motion, AnimatePresence } from'framer-motion';
import { BookOpen, Brain, Heart, Compass, Sun, Moon, Sparkles, Star, Award, ExternalLink, ChevronDown, Trophy, CheckCircle, XCircle, Zap, Crown } from'lucide-react';
import { meditationStages, hekhalotTexts } from'../../data/frameworks';
import { MeditationTracker } from'../interactive/MeditationTracker';

// Quiz questions testing knowledge from Steps 1-5
const quizQuestions = [
 {
 id: 1,
 question:'What prophet received the foundational vision of the Merkavah?',
 options: ['Isaiah','Ezekiel','Daniel','Elijah'],
 correctIndex: 1,
 explanation:'Ezekiel received the vision of the Divine Chariot by the river Chebar during the Babylonian exile.',
 hebrewHint:'יחזקאל'
 },
 {
 id: 2,
 question:'How many faces do the Chayot (living creatures) have?',
 options: ['Two','Three','Four','Seven'],
 correctIndex: 2,
 explanation:'Each Chayot has four faces: lion, ox, eagle, and human, representing different aspects of creation.',
 hebrewHint:'ארבע פנים'
 },
 {
 id: 3,
 question:'What are the Ophanim known for in Merkavah imagery?',
 options: [
'Their golden wings',
'Their many eyes',
'Their flaming swords',
'Their singing voices'
 ],
 correctIndex: 1,
 explanation:'The Ophanim (wheels) are covered with eyes, symbolizing divine omniscience and awareness.',
 hebrewHint:'עיניים'
 },
 {
 id: 4,
 question:'How many heavens must the mystic traverse in the Merkavah journey?',
 options: ['Three','Five','Seven','Ten'],
 correctIndex: 2,
 explanation:'The mystical ascent passes through seven heavens, each guarded by angelic beings.',
 hebrewHint:'שבעה רקיעים'
 },
 {
 id: 5,
 question:'What does the Hebrew word"Kavod" refer to in Merkavah mysticism?',
 options: ['The chariot wheels','Divine glory/presence','The guardian angels','The holy fire'],
 correctIndex: 1,
 explanation:'Kavod refers to the Divine Glory that rests upon the Throne, the ultimate goal of mystical contemplation.',
 hebrewHint:'כבוד'
 }
];

// Practice scenarios
const practiceScenarios = [
 {
 id:'meditation',
 title:'Contemplative Focus',
 description:'You are beginning a meditation on the Merkavah.',
 question:'What should be your primary focus?',
 options: [
 { text:'Achieving supernatural powers', feedback:'The goal is not powers but divine connection. Try again.' },
 { text:'Aligning the soul with its divine source', feedback:'Correct! The purpose is spiritual alignment and transformation.' },
 { text:'Memorizing the angelic names', feedback:'Names are tools, not the goal itself.' },
 { text:'Escaping physical reality', feedback:'Merkavah is about elevation, not escape from embodied life.' }
 ],
 correctIndex: 1
 },
 {
 id:'ethics',
 title:'Ethical Foundation',
 description:'A student wishes to study Merkavah mysticism.',
 question:'What prerequisite was traditionally required?',
 options: [
 { text:'Wealth and social status', feedback:'Material status was never a requirement for mystical study.' },
 { text:'Mastery of Torah and ethical refinement', feedback:'Correct! The Talmud required scholars of proven character and Torah knowledge.' },
 { text:'Being born into a priestly family', feedback:'While helpful, this was not the essential requirement.' },
 { text:'Having prophetic visions', feedback:'Visions were the goal, not the prerequisite.' }
 ],
 correctIndex: 1
 },
 {
 id:'symbolism',
 title:'Symbol Interpretation',
 description:'You encounter the image of the four-faced Chayot.',
 question:'What do the four faces primarily represent?',
 options: [
 { text:'Four historical periods', feedback:'The faces are not primarily temporal symbols.' },
 { text:'Four kings of Israel', feedback:'The symbolism is more universal than specific kings.' },
 { text:'Four aspects of divine service and creation', feedback:'Correct! Lion (love/chesed), Ox (strength/gevurah), Eagle (harmony/tiferet), Human (sovereignty/malkhut).' },
 { text:'Four elements: earth, water, fire, air', feedback:'While related, this is only part of the deeper meaning.' }
 ],
 correctIndex: 2
 }
];

// Resource library items
const resourceLibrary = [
 {
 id:'biblical',
 title:'Biblical Foundations',
 icon:'📜',
 topics: [
'Ezekiel chapters 1 and 10',
'Isaiah chapter 6 - throne vision',
'Daniel chapter 7 - Ancient of Days',
'1 Kings 22 - divine council'
 ]
 },
 {
 id:'hekhalot',
 title:'Hekhalot Literature',
 icon:'📚',
 topics: [
'Hekhalot Rabbati (Greater Palaces)',
'Hekhalot Zutarti (Lesser Palaces)',
'Ma\'aseh Merkavah (Work of the Chariot)',
'Sefer Yetzirah connections'
 ]
 },
 {
 id:'practice',
 title:'Contemplative Practices',
 icon:'🧘',
 topics: [
'Hitbonenut (contemplation)',
'Kedushah meditation',
'Visualization techniques',
'Sacred chanting and repetition'
 ]
 },
 {
 id:'symbolism',
 title:'Symbolic Meanings',
 icon:'✨',
 topics: [
'The four directions and faces',
'Colors of divine light',
'Numbers in Merkavah imagery',
'Connection to Sefirot'
 ]
 },
 {
 id:'modern',
 title:'Modern Applications',
 icon:'🌟',
 topics: [
'Jewish meditation revival',
'Academic Merkavah studies',
'Integration with Kabbalah',
'Contemporary spiritual practice'
 ]
 }
];

// Related topics from the network
const relatedTopics = [
 { name:'Geometry Knowledge', domain:'geometryknowledge.com', description:'Sacred geometry patterns' },
 { name:'Godly Library', domain:'godlylibrary.com', description:'Divine wisdom archive' },
 { name:'Global Knowledge Graph', domain:'globalknowledgegraph.com', description:'Connected wisdom network' },
 { name:'Truth Graph', domain:'truthgraph.com', description:'Philosophical truth exploration' }
];

// Knowledge map concepts for card-based display
const knowledgeMapConcepts = [
 { id:'merkavah', label:'Merkavah', hebrew:'מרכבה', group:'center', description:'The Divine Chariot, the central mystery of early Jewish mysticism. The vehicle of divine manifestation.' },
 { id:'chayot', label:'Chayot', hebrew:'חיות', group:'beings', description:'The four living creatures bearing the chariot, each with four faces representing different aspects of creation.' },
 { id:'ophanim', label:'Ophanim', hebrew:'אופנים', group:'beings', description:'The wheel-beings covered with eyes, symbolizing divine omniscience and the multi-dimensional nature of reality.' },
 { id:'throne', label:'Throne', hebrew:'כסא', group:'structure', description:'The Throne of Glory (Kisei HaKavod) upon which the divine presence rests in radiant majesty.' },
 { id:'heavens', label:'Seven Heavens', hebrew:'שבעה רקיעים', group:'structure', description:'The seven levels of heaven through which the mystic ascends, each with unique guardians and challenges.' },
 { id:'kavod', label:'Kavod', hebrew:'כבוד', group:'divine', description:'The Divine Glory, the visible manifestation of the infinite, the ultimate goal of Merkavah contemplation.' },
 { id:'kedushah', label:'Kedushah', hebrew:'קדושה', group:'practice', description:'The holy declaration sung by the angels:"Holy, Holy, Holy is the Lord of Hosts."' },
 { id:'meditation', label:'Meditation', hebrew:'התבוננות', group:'practice', description:'Contemplative practices for experiencing the chariot vision through disciplined visualization.' },
 { id:'sefirot', label:'Sefirot', hebrew:'ספירות', group:'kabbalah', description:'The ten divine emanations, the structural framework of Kabbalistic cosmology rooted in Merkavah imagery.' },
 { id:'ezekiel', label:'Ezekiel', hebrew:'יחזקאל', group:'source', description:'The prophet who received and recorded the foundational vision by the river Chebar in Babylonian exile.' }
];

const colorScale: { [key: string]: string } = {
 center:'from-orange-500 to-orange-600',
 beings:'from-purple-500 to-purple-600',
 structure:'from-blue-500 to-blue-600',
 divine:'from-yellow-500 to-amber-500',
 practice:'from-emerald-500 to-emerald-600',
 kabbalah:'from-pink-500 to-pink-600',
 source:'from-indigo-500 to-indigo-600'
};

export default function Step6() {
 const [activeStage, setActiveStage] = useState<number | null>(null);

 // Quiz state
 const [quizStarted, setQuizStarted] = useState(false);
 const [currentQuestion, setCurrentQuestion] = useState(0);
 const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
 const [showExplanation, setShowExplanation] = useState(false);
 const [quizScore, setQuizScore] = useState(0);
 const [quizComplete, setQuizComplete] = useState(false);
 const [answeredQuestions, setAnsweredQuestions] = useState<boolean[]>([]);

 // Practice scenarios state
 const [activeScenario, setActiveScenario] = useState<string | null>(null);
 const [scenarioAnswer, setScenarioAnswer] = useState<number | null>(null);
 const [scenarioFeedback, setScenarioFeedback] = useState<string | null>(null);
 const [completedScenarios, setCompletedScenarios] = useState<string[]>([]);

 // Resource library state
 const [expandedResources, setExpandedResources] = useState<string[]>([]);

 // Achievement state
 const [showAchievement, setShowAchievement] = useState(false);

 // Knowledge map state
 const [selectedConcept, setSelectedConcept] = useState<string | null>(null);

 const practicalApplications = [
 {
 icon: BookOpen,
 title:'Torah Study',
 description:'The contemplation of Ezekiel\'s vision was considered the pinnacle of Torah study. Understanding the chariot requires deep engagement with the entire biblical tradition.',
 practices: [
'Study Ezekiel 1 and 10 in the original Hebrew',
'Learn the commentaries of the Talmud and medieval sages',
'Explore the connections to other throne visions in scripture'
 ]
 },
 {
 icon: Brain,
 title:'Contemplative Practice',
 description:'The chariot was visualized as a focus for meditation, training the mind to hold complex symbolic images while maintaining spiritual awareness.',
 practices: [
'Develop capacity for sustained concentration',
'Practice visualizing the chariot components sequentially',
'Learn to maintain awareness while imagination is active'
 ]
 },
 {
 icon: Heart,
 title:'Ethical Refinement',
 description:'The four faces of the Chayot represent qualities to cultivate: the love of the lion, the strength of the ox, the vision of the eagle, and the consciousness of the human.',
 practices: [
'Develop expansive loving-kindness (lion/chesed)',
'Cultivate disciplined restraint (ox/gevurah)',
'Seek balanced harmony in all things (eagle/tiferet)',
'Awaken full conscious presence (human/malkhut)'
 ]
 },
 {
 icon: Compass,
 title:'Cosmic Orientation',
 description:'The chariot provides a map of reality - from the earthly realm through the seven heavens to the Throne of Glory. Understanding this map orients the soul.',
 practices: [
'Recognize your place in the cosmic hierarchy',
'Understand the angelic orders and their functions',
'Align personal will with divine will'
 ]
 }
 ];

 // Quiz handlers
 const handleAnswerSelect = (index: number) => {
 if (showExplanation) return;
 setSelectedAnswer(index);
 };

 const handleCheckAnswer = () => {
 if (selectedAnswer === null) return;
 setShowExplanation(true);
 const newAnswered = [...answeredQuestions];
 newAnswered[currentQuestion] = selectedAnswer === quizQuestions[currentQuestion].correctIndex;
 setAnsweredQuestions(newAnswered);
 if (selectedAnswer === quizQuestions[currentQuestion].correctIndex) {
 setQuizScore(quizScore + 1);
 }
 };

 const handleNextQuestion = () => {
 if (currentQuestion < quizQuestions.length - 1) {
 setCurrentQuestion(currentQuestion + 1);
 setSelectedAnswer(null);
 setShowExplanation(false);
 } else {
 setQuizComplete(true);
 }
 };

 // Scenario handlers
 const handleScenarioAnswer = (scenarioId: string, optionIndex: number) => {
 const scenario = practiceScenarios.find(s => s.id === scenarioId);
 if (!scenario) return;

 setScenarioAnswer(optionIndex);
 setScenarioFeedback(scenario.options[optionIndex].feedback);

 if (optionIndex === scenario.correctIndex && !completedScenarios.includes(scenarioId)) {
 setCompletedScenarios([...completedScenarios, scenarioId]);
 }
 };

 // Resource toggle
 const toggleResource = (id: string) => {
 if (expandedResources.includes(id)) {
 setExpandedResources(expandedResources.filter(r => r !== id));
 } else {
 setExpandedResources([...expandedResources, id]);
 }
 };

 // Check for achievement
 useEffect(() => {
 if (quizComplete && completedScenarios.length >= 2 && expandedResources.length >= 3) {
 setShowAchievement(true);
 }
 }, [quizComplete, completedScenarios, expandedResources]);

 return (
 <div className="section-container relative">
 {/* Introduction */}
 <div className="max-w-4xl mx-auto mb-16">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center mb-12"
 >
 <p className="hebrew-text text-2xl md:text-3xl text-primary-600 dark:text-primary-400 mb-4">
 והיו הדברים האלה אשר אנכי מצוך היום על לבבך
 </p>
 <p className="text-lg text-[var(--color-muted)] italic">
"And these words which I command you today shall be upon your heart."
 </p>
 <p className="text-sm text-[var(--color-muted)] mt-2">- Deuteronomy 6:6</p>
 </motion.div>

 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="prose prose-lg dark:prose-invert mx-auto"
 >
 <p>
 The study of the Merkavah was never merely academic. The ancient mystics
 sought to internalize the vision, to make the ascent not just in imagination
 but in the transformation of consciousness itself. The chariot becomes
 a vehicle for spiritual elevation when its symbols are embodied in practice.
 </p>
 </motion.div>
 </div>

 {/* Meditation Tracker - Heavenly Ascent Practice */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.28 }}
 className="mb-16 max-w-5xl mx-auto"
 >
 <MeditationTracker />
 </motion.div>

 {/* Interactive Summary Quiz */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3 }}
 className="card mb-16"
 >
 <div className="flex items-center gap-3 mb-6">
 <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white">
 <Star className="w-6 h-6" />
 </div>
 <div>
 <h3 className="text-xl font-semibold">Knowledge Check Quiz</h3>
 <p className="text-sm text-[var(--color-muted)]">Test your understanding from Steps 1-5</p>
 </div>
 </div>

 {!quizStarted ? (
 <div className="text-center py-8">
 <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-primary-600/20 to-secondary-700/20 flex items-center justify-center">
 <Trophy className="w-12 h-12 text-primary-600" />
 </div>
 <p className="text-[var(--color-muted)] mb-6">
 Ready to test what you have learned about Merkavah mysticism?
 Answer 5 questions to deepen your understanding!
 </p>
 <button
 onClick={() => setQuizStarted(true)}
 className="px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-700 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
 >
 <Zap className="w-5 h-5" />
 Start Quiz
 </button>
 </div>
 ) : quizComplete ? (
 <motion.div
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 className="text-center py-8"
 >
 <div className={`w-28 h-28 mx-auto mb-6 rounded-full flex items-center justify-center ${
 quizScore >= 4 ?'bg-gradient-to-r from-amber-400 to-yellow-500' :
 quizScore >= 3 ?'bg-gradient-to-r from-primary-600 to-secondary-700' :
'bg-gradient-to-r from-gray-400 to-gray-500'
 }`}>
 <div className="text-center text-white">
 <span className="text-4xl font-bold">{quizScore}</span>
 <span className="text-lg">/5</span>
 </div>
 </div>
 <h4 className="text-2xl font-semibold mb-2">
 {quizScore === 5 ?'Perfect Score! Mazal Tov!' : quizScore >= 4 ?'Excellent Understanding!' : quizScore >= 3 ?'Well Done!' :'Keep Studying!'}
 </h4>
 <p className="hebrew-text text-xl text-primary-600 dark:text-primary-400 mb-4">
 {quizScore === 5 ?'מזל טוב' : quizScore >= 3 ?'יישר כח' :'התחזק'}
 </p>
 <p className="text-[var(--color-muted)] mb-6">
 You answered {quizScore} out of 5 questions correctly.
 {quizScore >= 3 &&' You are ready to continue your mystical journey!'}
 </p>

 {/* Score breakdown */}
 <div className="flex justify-center gap-2 mb-6">
 {answeredQuestions.map((correct, idx) => (
 <div
 key={idx}
 className={`w-10 h-10 rounded-full flex items-center justify-center ${
 correct ?'bg-green-500' :'bg-red-400'
 } text-white`}
 >
 {correct ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
 </div>
 ))}
 </div>

 <button
 onClick={() => {
 setQuizStarted(false);
 setCurrentQuestion(0);
 setQuizScore(0);
 setQuizComplete(false);
 setAnsweredQuestions([]);
 setSelectedAnswer(null);
 setShowExplanation(false);
 }}
 className="px-6 py-3 text-primary-600 hover:bg-primary-600/10 rounded-xl transition-all duration-300 border border-primary-600/30"
 >
 Retake Quiz
 </button>
 </motion.div>
 ) : (
 <div>
 {/* Progress bar */}
 <div className="flex gap-2 mb-6">
 {quizQuestions.map((_, idx) => (
 <div
 key={idx}
 className={`h-2 flex-1 rounded-full transition-all duration-300 ${
 idx === currentQuestion
 ?'bg-primary-600'
 : idx < currentQuestion
 ? answeredQuestions[idx]
 ?'bg-green-500'
 :'bg-red-400'
 :'bg-gray-200 dark:bg-gray-700'
 }`}
 />
 ))}
 </div>

 <div className="flex items-center justify-between mb-4">
 <p className="text-sm text-[var(--color-muted)]">
 Question {currentQuestion + 1} of {quizQuestions.length}
 </p>
 <p className="hebrew-text text-lg text-primary-600 dark:text-primary-400">
 {quizQuestions[currentQuestion].hebrewHint}
 </p>
 </div>

 <h4 className="text-xl font-medium mb-6">
 {quizQuestions[currentQuestion].question}
 </h4>

 <div className="grid gap-3">
 {quizQuestions[currentQuestion].options.map((option, idx) => (
 <motion.button
 key={idx}
 onClick={() => handleAnswerSelect(idx)}
 disabled={showExplanation}
 whileHover={!showExplanation ? { scale: 1.01 } : {}}
 whileTap={!showExplanation ? { scale: 0.99 } : {}}
 className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-300 flex items-center gap-4 ${
 showExplanation
 ? idx === quizQuestions[currentQuestion].correctIndex
 ?'border-green-500 bg-green-500/10'
 : selectedAnswer === idx
 ?'border-red-500 bg-red-500/10'
 :'border-[var(--color-border)] opacity-50'
 : selectedAnswer === idx
 ?'border-primary-500 bg-primary-500/10'
 :'border-[var(--color-border)] hover:border-primary-500/50'
 }`}
 >
 <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
 showExplanation
 ? idx === quizQuestions[currentQuestion].correctIndex
 ?'bg-green-500 text-white'
 : selectedAnswer === idx
 ?'bg-red-500 text-white'
 :'bg-gray-200 dark:bg-gray-700'
 : selectedAnswer === idx
 ?'bg-primary-600 text-white'
 :'bg-gray-200 dark:bg-gray-700'
 }`}>
 {String.fromCharCode(65 + idx)}
 </div>
 <span className="flex-1">{option}</span>
 {showExplanation && idx === quizQuestions[currentQuestion].correctIndex && (
 <CheckCircle className="w-6 h-6 text-green-500" />
 )}
 {showExplanation && selectedAnswer === idx && idx !== quizQuestions[currentQuestion].correctIndex && (
 <XCircle className="w-6 h-6 text-red-500" />
 )}
 </motion.button>
 ))}
 </div>

 <AnimatePresence>
 {showExplanation && (
 <motion.div
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:'auto' }}
 exit={{ opacity: 0, height: 0 }}
 className="mt-6 p-4 rounded-xl bg-primary-600/10 border border-primary-600/30"
 >
 <p className="text-primary-700 dark:text-primary-300">
 <span className="font-semibold">Explanation:</span> {quizQuestions[currentQuestion].explanation}
 </p>
 </motion.div>
 )}
 </AnimatePresence>

 <div className="flex justify-end gap-3 mt-6">
 {!showExplanation ? (
 <button
 onClick={handleCheckAnswer}
 disabled={selectedAnswer === null}
 className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
 selectedAnswer === null
 ?'bg-gray-200 text-gray-400 cursor-not-allowed'
 :'bg-gradient-to-r from-primary-600 to-secondary-700 text-white hover:shadow-lg'
 }`}
 >
 Check Answer
 </button>
 ) : (
 <button
 onClick={handleNextQuestion}
 className="px-8 py-3 bg-gradient-to-r from-primary-600 to-secondary-700 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center gap-2"
 >
 {currentQuestion < quizQuestions.length - 1 ?'Next Question' :'See Results'}
 <Sparkles className="w-5 h-5" />
 </button>
 )}
 </div>
 </div>
 )}
 </motion.div>

 {/* Knowledge Map - Card-based */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 }}
 className="card mb-16"
 >
 <div className="flex items-center gap-3 mb-6">
 <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white">
 <Compass className="w-6 h-6" />
 </div>
 <div>
 <h3 className="text-xl font-semibold">Merkavah Knowledge Map</h3>
 <p className="text-sm text-[var(--color-muted)]">Click on concepts to explore their meanings</p>
 </div>
 </div>

 {/* Legend */}
 <div className="flex flex-wrap gap-3 mb-6 justify-center text-xs">
 {[
 { color:'bg-orange-500', label:'Core' },
 { color:'bg-purple-500', label:'Beings' },
 { color:'bg-blue-500', label:'Structure' },
 { color:'bg-yellow-500', label:'Divine' },
 { color:'bg-emerald-500', label:'Practice' },
 { color:'bg-pink-500', label:'Kabbalah' },
 { color:'bg-indigo-500', label:'Source' },
 ].map(({ color, label }) => (
 <div key={label} className="flex items-center gap-2">
 <div className={`w-3 h-3 rounded-full ${color}`} />
 <span className="text-[var(--color-muted)]">{label}</span>
 </div>
 ))}
 </div>

 {/* Concept Cards Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
 {knowledgeMapConcepts.map((concept) => (
 <motion.button
 key={concept.id}
 onClick={() => setSelectedConcept(selectedConcept === concept.id ? null : concept.id)}
 whileHover={{ scale: 1.03, y: -2 }}
 whileTap={{ scale: 0.98 }}
 className={`p-4 rounded-xl bg-gradient-to-br ${colorScale[concept.group]} text-white text-center transition-all duration-300 ${
 selectedConcept === concept.id ?'ring-2 ring-white shadow-xl' :'hover:shadow-lg'
 }`}
 >
 <p className="hebrew-text text-lg mb-1">{concept.hebrew}</p>
 <p className="text-sm font-medium">{concept.label}</p>
 </motion.button>
 ))}
 </div>

 {/* Selected Concept Detail */}
 <AnimatePresence>
 {selectedConcept && (
 <motion.div
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -10 }}
 className="mt-6 p-4 bg-primary-600/10 rounded-xl border border-primary-500/30"
 >
 <div className="flex items-center gap-3 mb-2">
 <span className="hebrew-text text-xl text-primary-600 dark:text-primary-400">
 {knowledgeMapConcepts.find(c => c.id === selectedConcept)?.hebrew}
 </span>
 <span className="text-lg font-semibold">
 {knowledgeMapConcepts.find(c => c.id === selectedConcept)?.label}
 </span>
 </div>
 <p className="text-[var(--color-muted)]">
 {knowledgeMapConcepts.find(c => c.id === selectedConcept)?.description}
 </p>
 </motion.div>
 )}
 </AnimatePresence>
 </motion.div>

 {/* Practice Exercises */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5 }}
 className="card mb-16"
 >
 <div className="flex items-center gap-3 mb-6">
 <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white">
 <Brain className="w-6 h-6" />
 </div>
 <div>
 <h3 className="text-xl font-semibold">Practice Exercises</h3>
 <p className="text-sm text-[var(--color-muted)]">
 Apply your learning ({completedScenarios.length}/3 completed)
 </p>
 </div>
 </div>

 <div className="grid md:grid-cols-3 gap-4">
 {practiceScenarios.map((scenario) => (
 <motion.button
 key={scenario.id}
 onClick={() => {
 setActiveScenario(activeScenario === scenario.id ? null : scenario.id);
 setScenarioAnswer(null);
 setScenarioFeedback(null);
 }}
 whileHover={{ scale: 1.02 }}
 whileTap={{ scale: 0.98 }}
 className={`p-4 rounded-xl border-2 text-left transition-all duration-300 ${
 activeScenario === scenario.id
 ?'border-primary-500 bg-primary-500/10'
 : completedScenarios.includes(scenario.id)
 ?'border-green-500 bg-green-500/10'
 :'border-[var(--color-border)] hover:border-primary-500/50'
 }`}
 >
 <div className="flex items-center justify-between mb-2">
 <h4 className="font-semibold">{scenario.title}</h4>
 {completedScenarios.includes(scenario.id) && (
 <CheckCircle className="w-5 h-5 text-green-500" />
 )}
 </div>
 <p className="text-sm text-[var(--color-muted)]">{scenario.description}</p>
 </motion.button>
 ))}
 </div>

 <AnimatePresence>
 {activeScenario && (
 <motion.div
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:'auto' }}
 exit={{ opacity: 0, height: 0 }}
 className="mt-6"
 >
 {(() => {
 const scenario = practiceScenarios.find(s => s.id === activeScenario);
 if (!scenario) return null;
 return (
 <div className="p-6 bg-[var(--color-card)] rounded-xl border border-[var(--color-border)]">
 <h4 className="font-semibold mb-4">{scenario.question}</h4>
 <div className="grid gap-3">
 {scenario.options.map((option, idx) => (
 <motion.button
 key={idx}
 onClick={() => handleScenarioAnswer(scenario.id, idx)}
 whileHover={{ scale: 1.01 }}
 className={`p-4 text-left rounded-xl border-2 transition-all duration-300 ${
 scenarioAnswer === idx
 ? idx === scenario.correctIndex
 ?'border-green-500 bg-green-500/10'
 :'border-red-500 bg-red-500/10'
 :'border-[var(--color-border)] hover:border-primary-500/50'
 }`}
 >
 {option.text}
 </motion.button>
 ))}
 </div>
 <AnimatePresence>
 {scenarioFeedback && (
 <motion.p
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className={`mt-4 p-4 rounded-xl ${
 scenarioAnswer === scenario.correctIndex
 ?'bg-green-500/10 text-green-700 dark:text-green-300 border border-green-500/30'
 :'bg-[#3366cc]/10 text-amber-700 dark:text-amber-300 border border-amber-500/30'
 }`}
 >
 {scenarioFeedback}
 </motion.p>
 )}
 </AnimatePresence>
 </div>
 );
 })()}
 </motion.div>
 )}
 </AnimatePresence>
 </motion.div>

 {/* Resource Library */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.6 }}
 className="card mb-16"
 >
 <div className="flex items-center gap-3 mb-6">
 <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white">
 <BookOpen className="w-6 h-6" />
 </div>
 <div>
 <h3 className="text-xl font-semibold">Resource Library</h3>
 <p className="text-sm text-[var(--color-muted)]">Expand your knowledge further ({expandedResources.length}/{resourceLibrary.length} explored)</p>
 </div>
 </div>

 <div className="grid gap-4">
 {resourceLibrary.map((resource) => (
 <motion.div
 key={resource.id}
 className="border border-[var(--color-border)] rounded-xl overflow-hidden"
 >
 <button
 onClick={() => toggleResource(resource.id)}
 className="w-full p-4 flex items-center justify-between hover:bg-[var(--color-card)] transition-all duration-300"
 >
 <div className="flex items-center gap-3">
 <span className="text-2xl">{resource.icon}</span>
 <span className="font-medium">{resource.title}</span>
 {expandedResources.includes(resource.id) && (
 <CheckCircle className="w-4 h-4 text-green-500" />
 )}
 </div>
 <ChevronDown
 className={`w-5 h-5 text-[var(--color-muted)] transition-transform duration-300 ${
 expandedResources.includes(resource.id) ?'rotate-180' :''
 }`}
 />
 </button>
 <AnimatePresence>
 {expandedResources.includes(resource.id) && (
 <motion.div
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:'auto' }}
 exit={{ opacity: 0, height: 0 }}
 className="px-4 pb-4"
 >
 <ul className="space-y-2 ml-10">
 {resource.topics.map((topic, idx) => (
 <li key={idx} className="flex items-center gap-2 text-[var(--color-muted)]">
 <Sparkles className="w-4 h-4 text-primary-600" />
 {topic}
 </li>
 ))}
 </ul>
 </motion.div>
 )}
 </AnimatePresence>
 </motion.div>
 ))}
 </div>
 </motion.div>

 {/* Meditation Stages */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Stages of <span className="text-flame">Contemplative Practice</span>
 </h2>

 <div className="max-w-4xl mx-auto">
 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
 {meditationStages.map((stage, index) => (
 <motion.button
 key={stage.stage}
 onClick={() => setActiveStage(activeStage === index ? null : index)}
 whileHover={{ scale: 1.05 }}
 whileTap={{ scale: 0.95 }}
 className={`p-4 rounded-xl text-center transition-all duration-300 ${
 activeStage === index
 ?'bg-gradient-to-r from-primary-600 to-secondary-700 text-white shadow-lg scale-105'
 :'bg-[var(--color-card)] border border-[var(--color-border)] hover:border-primary-600'
 }`}
 >
 <div className="text-2xl font-bold mb-1">{stage.stage}</div>
 <div className="text-xs font-medium">{stage.name}</div>
 </motion.button>
 ))}
 </div>

 <AnimatePresence>
 {activeStage !== null && (
 <motion.div
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:'auto' }}
 exit={{ opacity: 0, height: 0 }}
 className="card"
 >
 <div className="flex items-center gap-4 mb-4">
 <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white text-xl font-bold">
 {meditationStages[activeStage].stage}
 </div>
 <div>
 <h3 className="text-xl font-semibold">{meditationStages[activeStage].name}</h3>
 <p className="text-sm text-primary-600 dark:text-primary-400">
 {meditationStages[activeStage].meaning}
 </p>
 </div>
 </div>
 <p className="text-[var(--color-muted)] mb-4">
 {meditationStages[activeStage].description}
 </p>
 <div className="p-4 rounded-lg bg-primary-600/5 border border-primary-600/20">
 <h4 className="font-semibold mb-2 flex items-center gap-2">
 <Sparkles className="w-4 h-4 text-primary-600" />
 Practice
 </h4>
 <p className="text-sm text-[var(--color-muted)]">
 {meditationStages[activeStage].practice}
 </p>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 </motion.div>

 {/* Practical Applications */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Practical <span className="text-[#202122] font-bold font-serif">Applications</span>
 </h2>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
 {practicalApplications.map((app, index) => (
 <motion.div
 key={app.title}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5 + index * 0.1 }}
 whileHover={{ y: -4 }}
 className="card-glow group transition-all duration-300"
 >
 <div className="flex items-center gap-4 mb-4">
 <div className="p-3 rounded-xl bg-gradient-to-r from-primary-600/20 to-secondary-700/20">
 <app.icon className="w-8 h-8 text-primary-600" />
 </div>
 <h3 className="text-xl font-semibold">{app.title}</h3>
 </div>

 <p className="text-[var(--color-muted)] mb-4">{app.description}</p>

 <div className="space-y-2">
 {app.practices.map((practice, pIndex) => (
 <div
 key={pIndex}
 className="flex items-start gap-2 text-sm text-[var(--color-muted)]"
 >
 <Sparkles className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
 <span>{practice}</span>
 </div>
 ))}
 </div>
 </motion.div>
 ))}
 </div>
 </motion.div>

 {/* Continue Learning Section */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.7 }}
 className="card mb-16"
 >
 <div className="flex items-center gap-3 mb-6">
 <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white">
 <ExternalLink className="w-6 h-6" />
 </div>
 <div>
 <h3 className="text-xl font-semibold">Continue Learning</h3>
 <p className="text-sm text-[var(--color-muted)]">Explore related topics in our network</p>
 </div>
 </div>

 <div className="grid md:grid-cols-2 gap-4">
 {relatedTopics.map((topic) => (
 <a
 key={topic.domain}
 href={`https://${topic.domain}`}
 target="_blank"
 rel="noopener noreferrer"
 className="p-4 rounded-xl border border-[var(--color-border)] hover:border-primary-500 hover:bg-primary-500/5 transition-all duration-300 group"
 >
 <div className="flex items-center justify-between mb-2">
 <h4 className="font-semibold group-hover:text-primary-600 transition-colors duration-300">{topic.name}</h4>
 <ExternalLink className="w-4 h-4 text-[var(--color-muted)] group-hover:text-primary-500 transition-colors duration-300" />
 </div>
 <p className="text-sm text-[var(--color-muted)]">{topic.description}</p>
 </a>
 ))}
 </div>
 </motion.div>

 {/* Essential Texts */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.6 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Essential <span className="text-flame">Texts</span>
 </h2>

 <div className="max-w-4xl mx-auto">
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-[var(--color-border)]">
 <th className="text-left py-4 px-4 font-semibold">Text</th>
 <th className="text-left py-4 px-4 font-semibold">Meaning</th>
 <th className="text-left py-4 px-4 font-semibold hidden md:table-cell">Period</th>
 <th className="text-left py-4 px-4 font-semibold hidden lg:table-cell">Description</th>
 </tr>
 </thead>
 <tbody>
 {hekhalotTexts.map((text, index) => (
 <motion.tr
 key={index}
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.7 + index * 0.05 }}
 className="border-b border-[var(--color-border)] hover:bg-[var(--color-card)] transition-colors duration-300"
 >
 <td className="py-4 px-4">
 <span className="font-medium text-primary-600 dark:text-primary-400">
 {text.name}
 </span>
 </td>
 <td className="py-4 px-4 text-[var(--color-muted)]">{text.meaning}</td>
 <td className="py-4 px-4 text-sm text-[var(--color-muted)] hidden md:table-cell">
 {text.period}
 </td>
 <td className="py-4 px-4 text-sm text-[var(--color-muted)] hidden lg:table-cell">
 {text.description}
 </td>
 </motion.tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 </motion.div>

 {/* Daily Practice */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.8 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Integration into <span className="text-[#202122] font-bold font-serif">Daily Life</span>
 </h2>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
 <motion.div whileHover={{ y: -2 }} className="card transition-all duration-300">
 <div className="flex items-center gap-3 mb-4">
 <Sun className="w-8 h-8 text-[#3366cc]" />
 <h3 className="text-xl font-semibold">Morning Practice</h3>
 </div>
 <ul className="space-y-3 text-[var(--color-muted)]">
 <li className="flex items-start gap-2">
 <span className="text-primary-600 font-bold">1.</span>
 <span>Begin with awareness of the divine presence (Modeh Ani)</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-primary-600 font-bold">2.</span>
 <span>Recite the Kedushah, joining the angelic praise</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-primary-600 font-bold">3.</span>
 <span>Contemplate one aspect of the chariot during prayer</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-primary-600 font-bold">4.</span>
 <span>Set intention to embody a quality of the Chayot</span>
 </li>
 </ul>
 </motion.div>

 <motion.div whileHover={{ y: -2 }} className="card transition-all duration-300">
 <div className="flex items-center gap-3 mb-4">
 <Moon className="w-8 h-8 text-secondary-600" />
 <h3 className="text-xl font-semibold">Evening Reflection</h3>
 </div>
 <ul className="space-y-3 text-[var(--color-muted)]">
 <li className="flex items-start gap-2">
 <span className="text-secondary-600 font-bold">1.</span>
 <span>Review the day through the lens of the four faces</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-secondary-600 font-bold">2.</span>
 <span>Consider: How did divine providence move today?</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-secondary-600 font-bold">3.</span>
 <span>Practice gratitude for the eyes that see all</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-secondary-600 font-bold">4.</span>
 <span>Prepare the soul for the journey of sleep</span>
 </li>
 </ul>
 </motion.div>
 </div>
 </motion.div>

 {/* Final Blessing */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.9 }}
 className="p-8 rounded-2xl bg-gradient-to-r from-primary-600/10 to-secondary-700/10 border border-primary-600/20"
 >
 <div className="max-w-2xl mx-auto text-center">
 <h3 className="text-2xl font-display font-bold mb-6">A Final Word</h3>

 <p className="text-[var(--color-muted)] mb-6">
 The study of the Merkavah is not about acquiring supernatural experiences
 or gaining special knowledge. It is about transforming consciousness
 through contemplation of the divine order, aligning the soul with its
 source, and participating consciously in the cosmic drama of creation
 and redemption.
 </p>

 <blockquote className="p-6 bg-[var(--color-background)] rounded-xl border border-[var(--color-border)]">
 <p className="hebrew-text text-xl text-primary-600 dark:text-primary-400 mb-4">
 ברוך אתה ה׳ אלהינו מלך העולם
 <br />
 אשר קדשנו במצותיו וצונו לעסוק בדברי תורה
 </p>
 <p className="text-[var(--color-muted)] italic">
"Blessed are You, Lord our God, King of the universe,
 who has sanctified us with His commandments
 and commanded us to engage in the words of Torah."
 </p>
 </blockquote>

 <p className="mt-6 text-sm text-[var(--color-muted)]">
 May your journey through the mysteries of the Merkavah bring you
 closer to the Source of all being, and may you ascend from strength
 to strength in understanding and devotion.
 </p>
 </div>
 </motion.div>

 {/* Achievement Badge */}
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 1 }}
 className="mt-12 text-center"
 >
 <motion.div
 whileHover={{ scale: 1.05 }}
 whileTap={{ scale: 0.95 }}
 className={`inline-block p-8 rounded-full cursor-pointer transition-all duration-300 ${
 showAchievement
 ?'bg-gradient-to-r from-yellow-400 to-yellow-600 shadow-xl'
 :'bg-gradient-to-r from-primary-600 to-secondary-700'
 }`}
 onClick={() => {
 if (showAchievement) {
 const shareText = `I completed the Merkavah Mysticism Journey! Quiz: ${quizScore}/5, Scenarios: ${completedScenarios.length}/3. #MerkavahKnowledge #JewishMysticism`;
 navigator.clipboard.writeText(shareText);
 }
 }}
 >
 {showAchievement ? (
 <Crown className="w-16 h-16 text-white" />
 ) : (
 <Star className="w-16 h-16 text-white" />
 )}
 </motion.div>
 <h3 className="text-2xl font-display font-bold mt-6 text-[#202122] font-bold font-serif">
 {showAchievement ?'Merkavah Scholar!' :'Journey Complete'}
 </h3>
 <p className="hebrew-text text-xl text-primary-600 dark:text-primary-400 mt-2">
 {showAchievement ?'חכם המרכבה' :'סיום המסע'}
 </p>
 <p className="text-[var(--color-muted)] mt-2">
 {showAchievement
 ?'You have mastered the fundamentals. Click badge to celebrate and copy share text!'
 :'Complete the quiz, scenarios, and explore resources to unlock your Scholar badge!'
 }
 </p>

 {/* Progress indicators */}
 <div className="flex justify-center gap-6 text-sm mt-6">
 <div className="text-center">
 <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center transition-all duration-300 ${
 quizComplete ?'bg-green-500 text-white' :'bg-gray-200 dark:bg-gray-700 text-gray-500'
 }`}>
 {quizComplete ? <CheckCircle className="w-6 h-6" /> : <Star className="w-5 h-5" />}
 </div>
 <span className="text-[var(--color-muted)] mt-1 block">Quiz</span>
 </div>
 <div className="text-center">
 <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center transition-all duration-300 ${
 completedScenarios.length >= 2 ?'bg-green-500 text-white' :'bg-gray-200 dark:bg-gray-700 text-gray-500'
 }`}>
 {completedScenarios.length >= 2 ? <CheckCircle className="w-6 h-6" /> : <span className="font-bold">{completedScenarios.length}/2</span>}
 </div>
 <span className="text-[var(--color-muted)] mt-1 block">Scenarios</span>
 </div>
 <div className="text-center">
 <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center transition-all duration-300 ${
 expandedResources.length >= 3 ?'bg-green-500 text-white' :'bg-gray-200 dark:bg-gray-700 text-gray-500'
 }`}>
 {expandedResources.length >= 3 ? <CheckCircle className="w-6 h-6" /> : <span className="font-bold">{expandedResources.length}/3</span>}
 </div>
 <span className="text-[var(--color-muted)] mt-1 block">Resources</span>
 </div>
 </div>
 </motion.div>
 </div>
 );
}
